/* Subject captions on the map: screen-space, so they stay readable at any zoom.
 * No boxes — the map already labels things with a small letterspaced line, and a
 * rectangle behind the cards reads as a selection bound rather than a group.
 */
import { useTranslation } from 'react-i18next';
import { CAPTION, clusters } from '../../engine/clusters';
import { byId, chapterDone, pad } from '../../engine/progress';
import { clusterEntry } from '../../engine/routes';
import { useApp } from '../../state/AppProvider';
import type { View } from '../../engine/graph';
import type { NodeDef } from '../../types';
import '../../styles/subjects.css';

export function SubjectLabels({ view }: { view: View }) {
  const { t } = useTranslation();
  const { progress, selectNode } = useApp();

  /* Below this zoom the band cannot hold the caption, so it is not drawn. */
  if (view.k < CAPTION.minZoom) return null;

  return (
    <div className="subject-labels" data-od-id="subject-labels">
      {clusters().map((cluster) => {
        const chapters = cluster.chapters.map(byId).filter((n): n is NodeDef => !!n);
        const done = chapters.filter((n) => chapterDone(n, progress)).length;
        const entry = clusterEntry(cluster, progress);
        const blocked = entry.kind === 'blocked';
        const assumes =
          blocked && entry.by ? `${t('after')} ${pad(entry.by)} · ${t(entry.by.titleKey)}` : '';

        return (
          <button
            key={cluster.id}
            type="button"
            className="subject-caption"
            data-act="subject"
            data-id={cluster.id}
            data-done={entry.kind === 'done' ? 'true' : undefined}
            style={{
              left: cluster.x * view.k + view.x,
              top: cluster.y * view.k + view.y + 1,
            }}
            title={assumes || undefined}
            aria-label={`${t(cluster.labelKey)} · ${t('routes:done', { done, total: chapters.length })}${assumes ? ` · ${assumes}` : ''}`}
            onClick={() => {
              if (entry.kind === 'done') return;
              selectNode(entry.chapter.id);
            }}
          >
            <span className="subject-name">{t(cluster.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
}
